import { Button, Checkbox, Icon, Modal } from "semantic-ui-react"
import styled from "styled-components"

import { DndContext } from "components/lib/dnd/DndContext"
import { Draggable } from "components/lib/dnd/Draggable"
import { Droppable } from "components/lib/dnd/Droppable"
import { moveItem } from "components/lib/dnd/dndHelpers"

import type { Column } from "./types"

const ReorderableRow = styled.div`
  user-select: none;
  padding: 10px 0;
  margin: 0 0 5px 0;
  border-radius: 5px;
  cursor: pointer;
`

type TableSettingsProps<ItemType extends Record<string, any>> = {
  open: boolean
  onClose: () => void
  columns: Column<ItemType>[]
  updateColumns: (newColumns: Column<ItemType>[] | null) => void
}

const TableSettings = <ItemType extends Record<string, any>>({
  open,
  onClose,
  columns,
  updateColumns,
}: TableSettingsProps<ItemType>) => {
  const updateColumn = (key, newColumn) => {
    const index = columns.findIndex((col) => col.key === key)
    if (index === -1) return

    const newColumns = [...columns]
    newColumns[index] = newColumn
    updateColumns(newColumns)
  }

  return (
    <DndContext
      onDragEnd={(event) => {
        const order = columns.map((_, i) => i)
        moveItem(event, {
          droppable: {
            items: order,
            directSetter: (newOrder) => {
              updateColumns(columns.map((col, i) => ({ ...col, order: newOrder.indexOf(i) })))
            },
          },
        })
      }}
    >
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={open}
        closeOnDimmerClick
        onClose={onClose}
      >
        <Modal.Header>Update table</Modal.Header>
        <Modal.Content scrolling>
          <h3>Selected columns</h3>
          <p>
            Table columns can be modified here. Use the toggle to only display information that is relevant to you.
            Table columns can also be reordered via drag and drop.
          </p>
          <p>These settings will be synchronised between devices.</p>
          <Droppable id="droppable" itemIds={columns.map((col) => col.key)}>
            <div style={{ padding: 10 }}>
              {columns.map((column) => (
                <Draggable key={column.key} id={column.key}>
                  <ReorderableRow>
                    <Icon name="bars" style={{ marginLeft: 10, marginRight: 10 }} />
                    <Checkbox
                      toggle
                      checked={column.enabled}
                      onChange={(_e, check) => {
                        if ((check.checked && !column.enabled) || (!check.checked && column.enabled)) {
                          updateColumn(column.key as string, { ...column, enabled: !!check.checked })
                        }
                      }}
                      label={column.settingName ?? column.name}
                    />
                  </ReorderableRow>
                </Draggable>
              ))}
            </div>
          </Droppable>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => updateColumns(null)}>Reset to default</Button>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button content="Done" labelPosition="right" icon="checkmark" onClick={onClose} />
        </Modal.Actions>
      </Modal>
    </DndContext>
  )
}

export default TableSettings
